import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Alert,
  AlertIcon,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Card,
  CardBody,
  CardHeader,
  Center,
  Divider,
  Flex,
  Heading,
  Skeleton,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  Badge,
} from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import { FiExternalLink, FiPlusSquare } from 'react-icons/all';
import { getDate, getFrontendURL } from '../../utils/utils';
import { setUser } from '../../features/user/userSlice';
import { getSitesByCreator, getUser } from '../../api/siteApi';

function SitePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [sites, setSites] = useState([]);
  const [error, setError] = useState('');
  const [shouldShowAddButton, setShouldShowAddButton] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    fetchUser();
    // eslint-disable-next-line
  }, []);

  const fetchUser = () => {
    const id = new URLSearchParams(window.location.search).get('id');
    const source = new URLSearchParams(window.location.search).get('source');
    if (!id) {
      setIsLoading(false);
      setError('Id is missing');
      return;
    }
    getUser(id)
      .then(response => {
        if (response !== null && response.user !== null) {
          const userResponse = response.user;
          dispatch(setUser(userResponse));
          fetchSites(userResponse._id, source);
          setShouldShowAddButton(userResponse.level !== undefined);
        } else {
          setIsLoading(false);
          setError('User Not found');
        }
      })
      .catch(error => {
        console.log(error);
        setIsLoading(false);
        setError('User Not found');
      });
  };

  const fetchSites = (userId, source) => {
    if (!userId) {
      setIsLoading(false);
      return;
    }
    getSitesByCreator(userId, source)
      .then(response => {
        if (response !== null && response.sites.length > 0) {
          let sites = [];
          response.sites.forEach(site => {
            sites.push({
              id: site._id,
              title: site.title,
              status: site.status,
              expiryDate: site.expiryDate,
              url: site.url,
              owner: site.owner,
              creatorDetails: site.creatorDetails,
            });
          });
          setSites(sites);
          setIsLoading(false);
        } else {
          setShouldShowAddButton(true);
          setSites([]);
          setIsLoading(false);
        }
      })
      .catch(error => {
        console.log(error);
        setIsLoading(false);
        setSites([]);
      });
  };

  const onEdit = index => {
    navigate(`edit?id=${sites[index].id}`);
  };

  return (
    <>
      <Breadcrumb marginTop={2} marginLeft={2}>
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to="/">
            Home
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to="/sites">
            Sites
          </BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>
      <Stack mt={4} direction={{ base: 'column', lg: 'row' }}>
        <Flex flex={1}>
          {/* TODO: #17  Update Skeleton view to match the card structure */}
          {isLoading ? (
            <Skeleton height={'500px'} width={'100%'} />
          ) : (
            <Box w={'100%'}>
              <Box display={error ? 'block' : 'none'}>
                <Alert status="error" display={error ? 'flex' : 'none'}>
                  <AlertIcon />
                  {error}
                </Alert>
                <Center>
                  <Button
                    marginTop={2}
                    colorScheme="teal"
                    variant="outline"
                    onClick={() => console.log('backClicked')}
                  >
                    Back
                  </Button>
                </Center>
              </Box>
              <Card bg={'white'} display={error ? 'none' : 'block'}>
                <CardHeader>
                  <Flex spacing="4">
                    <Heading size="md">Sites</Heading>
                    <Button
                      ml="auto"
                      leftIcon={<FiPlusSquare />}
                      display={shouldShowAddButton ? 'flex' : 'none'}
                      colorScheme="teal"
                      variant="solid"
                      onClick={() => navigate('add')}
                    >
                      Add site
                    </Button>
                  </Flex>
                </CardHeader>
                <Divider />
                <CardBody>
                  {sites.length === 0 ? (
                    <Alert status="info">
                      <AlertIcon />
                      No Sites found
                    </Alert>
                  ) : (
                    <TableContainer>
                      <Table variant="simple">
                        <Thead>
                          <Tr>
                            <Th>Sr. No</Th>
                            <Th>Title</Th>
                            <Th>Owner</Th>
                            <Th>Creator</Th>
                            <Th>URL</Th>
                            <Th>Expiry Date</Th>
                            <Th>Actions</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {sites.map((site, index) => {
                            return (
                              <Tr key={`${site.name}-${index}`}>
                                <Td>{index + 1}</Td>
                                <Td>{site.title}</Td>
                                <Td>
                                  <Box>
                                    <Text>{site.owner?.name || 'N/A'}</Text>
                                    <Badge
                                      colorScheme={
                                        site.owner?.role === 'Admin'
                                          ? 'red'
                                          : site.owner?.role === 'Distributor'
                                            ? 'blue'
                                            : 'green'
                                      }
                                      variant="subtle"
                                      fontSize="xs"
                                    >
                                      {site.owner?.role || 'Unknown'}
                                    </Badge>
                                  </Box>
                                </Td>
                                <Td>
                                  <Box>
                                    <Text>
                                      {site.creatorDetails?.name || 'N/A'}
                                    </Text>
                                    <Badge
                                      colorScheme={
                                        site.creatorDetails?.role === 'Admin'
                                          ? 'red'
                                          : site.creatorDetails?.role ===
                                              'Distributor'
                                            ? 'blue'
                                            : 'green'
                                      }
                                      variant="subtle"
                                      fontSize="xs"
                                    >
                                      {site.creatorDetails?.role || 'Unknown'}
                                    </Badge>
                                  </Box>
                                </Td>
                                <Td>
                                  <a
                                    href={`${getFrontendURL()}/${site.url}`}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <FiExternalLink />
                                  </a>
                                </Td>
                                <Td>{getDate(site.expiryDate)}</Td>
                                <Td>
                                  <Stack direction="row">
                                    <Button
                                      colorScheme="teal"
                                      variant="solid"
                                      onClick={() => onEdit(index)}
                                    >
                                      Edit
                                    </Button>
                                  </Stack>
                                </Td>
                              </Tr>
                            );
                          })}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  )}
                </CardBody>
              </Card>
            </Box>
          )}
        </Flex>
      </Stack>
    </>
  );
}

export default SitePage;
